import {buildXml} from './exports.js';
import {sequenceClips} from './sequence.js';

// CMX 3600 is a single-video-track interchange format. Use XML for linked audio.
const pad=(n,width=2)=>String(n).padStart(width,'0');
const clean=name=>String(name||'').replace(/[\r\n]/g,' ').trim();

function rateInfo(media){
 const fps=media.fpsNum/media.fpsDen;
 const nominal=Math.round(fps);
 const ntsc=Math.abs(fps-nominal*1000/1001)<0.00001;
 if(![24,25,30].includes(nominal)||(!ntsc&&Math.abs(fps-nominal)>0.00001))
  throw new Error('Resolve EDL supports 23.976, 24, 25, 29.97 and 30 fps only. Use Resolve XML for other frame rates.');
 return {nominal,ntsc};
}
function parseTc(value,nominal,allowDrop){
 const match=/^(\d{2}):(\d{2}):(\d{2})([:;])(\d{2})$/.exec(value||'00:00:00:00');
 if(!match)throw new Error('Source timecode is invalid. Verify Source settings or use Resolve XML.');
 const h=+match[1],m=+match[2],s=+match[3],f=+match[5],drop=match[4]===';';
 if(h>23||m>59||s>59||f>=nominal)throw new Error('Source timecode is outside SMPTE bounds. Verify Source settings.');
 if(drop&&!allowDrop)throw new Error('Drop-frame EDL requires 29.97 fps footage. Use Resolve XML.');
 if(drop&&m%10!==0&&s===0&&f<2)throw new Error('Invalid dropped-frame source timecode label.');
 const minutes=h*60+m;
 return {drop,frame:(h*3600+m*60+s)*nominal+f-(drop?2*(minutes-Math.floor(minutes/10)):0)};
}
function printTc(frame,nominal,drop){
 const perDay=86400*nominal-(drop?2*(1440-144):0);
 let n=((frame%perDay)+perDay)%perDay;
 if(drop){
  const tens=Math.floor(n/17982),remain=n%17982;
  n+=18*tens+(remain>=2?2*Math.floor((remain-2)/1798):0);
 }
 return `${pad(Math.floor(n/(3600*nominal)))}:${pad(Math.floor(n/(60*nominal))%60)}:${pad(Math.floor(n/nominal)%60)}${drop?';':':'}${pad(n%nominal)}`;
}
function makeReels(entries){
 const names=new Map(),used=new Set();
 for(const item of entries){
  if(names.has(item.id))continue;
  const prefix=clean(item.source.filename).replace(/\.[^.]+$/,'').toUpperCase().replace(/[^A-Z0-9]/g,'')||'SOURCE';
  let name=prefix.slice(0,8),n=0;
  while(used.has(name)){
   if(++n>999)throw new Error('Too many similar reel names. Use Resolve XML.');
   name=prefix.slice(0,5).padEnd(5,'0')+pad(n,3);
  }
  names.set(item.id,name);used.add(name);
 }
 return names;
}
export function buildResolveEdl(project){
 // Reuse verified source metadata and frame-boundary validation from XML.
 buildXml(project);
 const paper=project.paperExport;
 const sequence=paper?.sequenceRate?{...project,...paper.sequenceRate}:project;
 const rate=rateInfo(sequence),nominal=rate.nominal;
 const parts=(paper?paper.blocks:sequenceClips(project)).map(part=>{
  const source=paper?paper.sources[part.sourceId]:project;
  const info=rateInfo(source);
  if(info.nominal!==rate.nominal||info.ntsc!==rate.ntsc||
     source.fpsNum*sequence.fpsDen!==sequence.fpsNum*source.fpsDen)
   throw new Error('Resolve EDL cannot represent mixed frame rates safely. Use Resolve XML.');
  const sourceTc=parseTc(source.timecode,nominal,rate.ntsc&&nominal===30);
  const start=Math.round(part.start*source.fpsNum/source.fpsDen);
  const end=Math.round(part.end*source.fpsNum/source.fpsDen);
  if(end<=start)throw new Error('An EDL passage is shorter than one frame.');
  return {source,id:part.sourceId||'single',sourceTc,start,end};
 });
 if(parts.length>999)throw new Error('CMX 3600 EDL supports at most 999 events. Use Resolve XML.');
 const drop=parts[0].sourceTc.drop;
 if(parts.some(part=>part.sourceTc.drop!==drop))
  throw new Error('An EDL cannot mix drop-frame and non-drop source timecodes. Use Resolve XML.');
 const reels=makeReels(parts);
 let cursor=parseTc(drop?'01:00:00;00':'01:00:00:00',nominal,drop).frame;
 const lines=[`TITLE: ${clean(project.title||'Takefold Assembly').slice(0,80)}`,
  `FCM: ${drop?'DROP FRAME':'NON-DROP FRAME'}`,''];
 for(let i=0;i<parts.length;i++){
  const part=parts[i],sourceIn=part.sourceTc.frame+part.start,sourceOut=part.sourceTc.frame+part.end;
  const recordOut=cursor+part.end-part.start;
  lines.push(`${pad(i+1,3)}  ${reels.get(part.id).padEnd(8)} V     C        ${printTc(sourceIn,nominal,drop)} ${printTc(sourceOut,nominal,drop)} ${printTc(cursor,nominal,drop)} ${printTc(recordOut,nominal,drop)}`);
  lines.push(`* FROM CLIP NAME: ${clean(part.source.filename)}`,'');
  cursor=recordOut;
 }
 return lines.join('\n')+'\n';
}
